import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, getRedirectPathForRole } from '../contexts/AuthContext';
import { toast } from '@/utils/portalNotifications';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { BRAND_LOGO_URL, branding } from '../config/branding';
import { sanitizeClientLoginNextPath } from '../utils/clientLoginRedirect';

const STAFF_PORTAL_MESSAGE = 'This account must sign in via the Staff/Admin portal.';

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session_expired') === '1';
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, false); // client portal -> /api/auth/login
      if (result.success) {
        const nextSafe = sanitizeClientLoginNextPath(searchParams.get('next'));
        if (nextSafe) {
          navigate(nextSafe);
        } else {
          const path = getRedirectPathForRole(result.user?.role);
          navigate(path);
        }
      } else {
        const msg = result.error || 'Login failed';
        setError(msg);
        if (result.status === 403 && msg === STAFF_PORTAL_MESSAGE) {
          toast.error(msg);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <img src={BRAND_LOGO_URL} alt={branding.companyName} className="h-10 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-midnight-blue">Sign In</CardTitle>
          <CardDescription>
            Enter your credentials to access your compliance portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {sessionExpired && (
              <Alert className="border-amber-200 bg-amber-50" data-testid="session-expired-alert">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <span className="font-medium text-amber-900">Session expired.</span>
                  <span className="block mt-1 text-amber-800">Please sign in again.</span>
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-electric-teal hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-electric-teal hover:bg-electric-teal/90" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-center text-sm text-gray-600 space-y-2">
              <p>
                Need an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/intake/start')}
                  className="text-electric-teal hover:underline font-medium"
                >
                  Get Started
                </button>
              </p>
              <p>
                <button
                  type="button"
                  onClick={() => (window.history.length > 2 ? navigate(-1) : navigate('/'))}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ← Back
                </button>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientLoginPage;
